import get from 'lodash/get';
import { trackEvent } from './segment';

export const extractStudioDesignProductData = ({
  studio,
  design,
  product,
  altProduct
}) => {
  const studioProperties = studio && studio.getSegmentProperties && studio.getSegmentProperties();
  const designProperties = design && design.getSegmentProperties && design.getSegmentProperties();
  const productProperties = product && product.getSegmentProperties && product.getSegmentProperties();
  const altProductProperties = altProduct && altProduct.getSegmentProperties && altProduct.getSegmentProperties();

  const studioData = {
    studio_id: get(studioProperties, 'id'),
    package_type: get(studioProperties, 'package_type')
    // studio_genome: , // not sure where to get this
  };

  const designData = {
    design_id: get(designProperties, 'id'),
    design_number: get(designProperties, 'number'),
    design_stylist: get(designProperties, 'stylist')
  };

  const productData = {
    product_id: get(productProperties, 'id'),
    product_vendor: get(productProperties, 'vendor'),
    product_price_point: get(productProperties, 'price_point'),
    product_genome: get(productProperties, 'genome'),
    product_price: get(productProperties, 'price'),
    promo_price: get(productProperties, 'promo_price'),
    vendor_price: get(productProperties, 'vendor_price'),
    product_color: get(productProperties, 'color'),
    is_assortment: get(productProperties, 'is_assortment'),
    vendor_type: get(productProperties, 'vendor_type'),
    product_type: get(productProperties, 'type'),
    product_name: get(productProperties, 'name'),
    is_kid_friendly: get(productProperties, 'is_kid_friendly'),
    is_pet_friendly: get(productProperties, 'is_pet_friendly'),
    has_3d_assets: get(productProperties, 'has_3d_assets')
    // promo_count: , // not sure what this is or where to get it
  };

  const altProductData = {
    alt_product_id: get(altProductProperties, 'id'),
    alt_product_vendor: get(altProductProperties, 'vendor'),
    alt_product_price_point: get(altProductProperties, 'price_point'),
    alt_product_genome: get(altProductProperties, 'genome'),
    alt_product_price: get(altProductProperties, 'price'),
    alt_product_promo_price: get(altProductProperties, 'promo_price'),
    alt_product_vendor_price: get(altProductProperties, 'vendor_price'),
    alt_product_product_color: get(altProductProperties, 'color'),
    alt_product_is_assortment: get(altProductProperties, 'is_assortment'),
    alt_product_vendor_type: get(altProductProperties, 'vendor_type'),
    alt_product_product_type: get(altProductProperties, 'type'),
    alt_product_product_name: get(altProductProperties, 'name'),
    alt_product_is_kid_friendly: get(altProductProperties, 'is_kid_friendly'),
    alt_product_is_pet_friendly: get(altProductProperties, 'is_pet_friendly')
    // alt_product_promo_count: , // not sure what this is or where to get it
  };

  // checking again for this data ensures we don't include
  // the keys if there's no data to report
  return {
    ...(studioProperties && studioData),
    ...(designProperties && designData),
    ...(productProperties && productData),
    ...(altProductProperties && altProductData)
  };
};

export const trackStudioVisit = ({
  analyticsPackageData
}) => {
  const {
    admin,
    designId,
    designNumber,
    designStylist,
    isSharedStudio,
    packagePrice,
    packageType,
    spaceId,
    studioGenome,
    userId
  } = analyticsPackageData;
  trackEvent({
    event: `studio_homepage_visit`,
    properties: {
      admin,
      design_id: designId,
      design_number: designNumber,
      design_stylist: designStylist,
      is_shared_studio: isSharedStudio,
      is_shoppable: !isSharedStudio,
      package_price: packagePrice,
      package_type: packageType,
      studio_id: spaceId,
      studio_genome: studioGenome,
      user_id: userId
    }
  });
};

export const trackStudioDesignFeedbackChangesRequested = ({
  design,
  designFeedbackChangeSelection,
  designFeedbackScore,
  studio
}) => {
  trackEvent({
    event: 'studio_design_feedback_changes_requested',
    properties: {
      ...extractStudioDesignProductData({studio, design}),
      design_feedback_change_selection: designFeedbackChangeSelection,
      design_feedback_score: designFeedbackScore
    }
  });
};

export const trackStudioDesignFeedbackFreeForm = ({
  design,
  designFeedbackScore,
  designFeedbackText,
  studio
}) => {
  trackEvent({
    event: 'studio_design_feedback_free_form',
    properties: {
      ...extractStudioDesignProductData({studio, design}),
      design_feedback_text: designFeedbackText,
      design_feedback_score: designFeedbackScore
    }
  });
};

export const trackStudioRedesignRequestClicked = ({
  studio,
  design,
  packagePrice
}) => {
  trackEvent({
    event: 'studio_request_changes',
    properties: {
      ...extractStudioDesignProductData({studio, design}),
      packagePrice
    }
  });
};

export const trackStudioHotSpotClicked = ({
  design,
  packagePrice,
  product,
  studio,
  userId
}) => {
  trackEvent({
    event: 'studio_hot_spot_clicked',
    properties: {
      ...extractStudioDesignProductData({ studio, design, product }),
      package_price: packagePrice,
      user_id: userId
    }
  });
};

export const trackStudioTapOnMobileRender = ({
  hotspotStatus,
  spaceId,
  userId
}) => {
  trackEvent({
    event: `studio_tap_hotspots_${hotspotStatus}`,
    properties: {
      space_id: spaceId,
      user_id: userId
    }
  });
};

export const trackStudioPDPView = ({
  design,
  packagePrice,
  product,
  productGridFilterNames,
  productIdOfPDPAlternativeReferral,
  referralSource,
  studio,
  tileCardPlaceInProductGrid
}) => {
  trackEvent({
    event: 'studio_pdp_view',
    properties: {
      ...extractStudioDesignProductData({ studio, design, product }),
      package_price: packagePrice,
      primary_referral_source: referralSource, // (hotspot, tile_card, PDP_alternative)
      tile_card_place_in_product_grid: tileCardPlaceInProductGrid,
      product_grid_filter_names: productGridFilterNames,
      product_id_of_pdp_alternative_referral: productIdOfPDPAlternativeReferral
    }
  });
};

export const trackStudioVariantClick = ({
  attributeTypeInfo,
  index,
  product,
  studio,
  parentProduct
}) => {
  trackEvent({
    event: 'studio_pdp_collection_products_clicked',
    properties: {
      ...extractStudioDesignProductData({ studio, product }),
      tile_card_place_in_product_grid: index,
      product_id_of_pdp_alternative_referral: parentProduct.id(),
      ...attributeTypeInfo
    }
  });
};

export const trackStudioAddToCart = ({
  design,
  origin,
  packagePrice,
  primaryReferralSource,
  product,
  productGridFilterNames,
  productIdOfPDPAlternativeReferral,
  searchOrigin,
  studio,
  tileCardPlaceInProductGrid,
  userId
}) => {
  trackEvent({
    event: 'studio_add_to_cart',
    properties: {
      ...extractStudioDesignProductData({ studio, design, product }),
      package_price: packagePrice,
      primary_referral_source: primaryReferralSource, // (tile_card, PDP, PDP_alternative_product, swap)
      tile_card_place_in_product_grid: tileCardPlaceInProductGrid,
      product_grid_filter_names: productGridFilterNames,
      product_id_of_pdp_alternative_referral: productIdOfPDPAlternativeReferral,
      // product_id_of_swapped_product: , // is this the product we swapped from, or to? do we even keep track of previous products?
      search_origin: searchOrigin,
      origin,
      userId
    }
  });
};

export const trackStudioSwap = ({
  design,
  origin,
  product,
  productGridFilterNames,
  productIdOfPDPAlternativeReferral,
  productIdOfSwappedProduct,
  searchOrigin,
  studio,
  tileCardPlaceInProductGrid,
  userId
}) => {
  trackEvent({
    event: 'studio_swap',
    properties: {
      ...extractStudioDesignProductData({ studio, design, product }),
      origin,
      product_grid_filter_names: productGridFilterNames,
      product_id_of_pdp_alternative_referral: productIdOfPDPAlternativeReferral,
      product_id_of_swapped_product: productIdOfSwappedProduct,
      search_origin: searchOrigin,
      tile_card_place_in_product_grid: tileCardPlaceInProductGrid,
      user_id: userId
    }
  });
};

export const track2dToggleClicked = ({
  enable2d
}) => {
  trackEvent({
    event: '2d_filter_event',
    properties: {
      enabled_2d_products: enable2d
    }
  });
};

export const trackStudioPDPAlternativeFiltersUsed = ({
  design,
  pdpAltFilterNames,
  product,
  studio
}) => {
  trackEvent({
    event: 'studio_pdp_alternative_filters_used',
    properties: {
      ...extractStudioDesignProductData({ studio, design, product }),
      pdp_alt_filter_names: pdpAltFilterNames
    }
  });
};

export const trackStudioPDPAlternativeProductsClicked = ({
  design,
  parentProduct,
  pdpAltFilterNames,
  product,
  studio
}) => {
  trackEvent({
    event: 'studio_pdp_alternative_products_clicked',
    properties: {
      ...extractStudioDesignProductData({studio, design, product: parentProduct, altProduct: product}),
      pdp_alt_filter_names: pdpAltFilterNames
    }
  });
};

export const trackStudioCartCouponApplied = ({
  cartTotal,
  couponAmount,
  couponText,
  designId,
  products,
  studioId
}) => {
  trackEvent({
    event: 'studio_cart_coupon_applied',
    properties: {
      cart_total: cartTotal,
      coupon_amount: couponAmount,
      coupon_text: couponText,
      design_id: designId,
      products: products.map((product) => ({
        ...(product.product() ? product.product().getSegmentProperties() : {}),
        quantity: product.fields && product.fields.quantity,
        subtotal: product.fields && product.fields.subtotal
      })),
      studio_id: studioId
    }
  });
};

export const trackStudioCartPageVisited = ({
  designId,
  studioId,
  userId
}) => {
  trackEvent({
    event: 'studio_cart_page_visited',
    properties: {
      design_id: designId,
      studio_id: studioId,
      user_id: userId
    }
  });
};

export const trackCheckoutPaymentFormOpened = ({
  items,
  userId
}) => {
  trackEvent({
    event: 'checkout_payment_form_opened',
    properties: {
      items,
      user_id: userId
    }
  });
};

export const trackStudioReviewOrderCtaClicked = ({
  cartSize,
  items,
  userId,
  spaceId
}) => {
  trackEvent({
    event: 'studio_review_order_cta_clicked',
    properties: {
      cart_size: cartSize,
      items,
      user_id: userId,
      studio_id: spaceId
    }
  });
};

export const trackCheckoutAddressError = ({
  event,
  userId,
  field,
  error
}) => {
  trackEvent({
    event,
    properties: {
      error_field: field,
      user_id: userId,
      error
    }
  });
};

export const trackCheckoutShippingAddressInput = ({
  event,
  items,
  userId
}) => {
  trackEvent({
    event,
    properties: {
      items,
      user_id: userId
    }
  });
};

export const trackCheckoutConfirmationCtaClicked = ({
  event,
  orderNumber,
  paymentMethod
}) => {
  trackEvent({
    event,
    properties: {
      order_number: orderNumber,
      payment_method: paymentMethod
    }
  });
};

export const trackStudioCartOrderFailed = ({
  items,
  message
}) => {
  trackEvent({
    event: 'studio_cart_order_failed',
    properties: {
      items,
      reason: message
    }
  });
};

export const trackStudioCartOrderCompleted = ({
  cartTotal,
  designId,
  items,
  paymentOption,
  products,
  shipAddress,
  shipCost,
  shipState,
  shipZip,
  studioId
}) => {
  trackEvent({
    event: 'studio_cart_order_completed',
    properties: {
      cart_total: cartTotal,
      design_id: designId,
      items,
      payment_option: paymentOption,
      products: products.map((product) => ({
        ...(product.product() ? product.product().getSegmentProperties() : {}),
        quantity: product.fields && product.fields.quantity,
        subtotal: product.fields && product.fields.subtotal
      })),
      ship_address: shipAddress,
      ship_cost: shipCost,
      ship_state: shipState,
      ship_zip: shipZip,
      studio_id: studioId
    }
  });
};

export const trackProductLikeDislikeClick = ({
  designId,
  dislikeReason,
  email,
  enabledFilters,
  isFromShop,
  isShowroom,
  likeStatus,
  origin,
  productIdOfPDPAlternativeReferral,
  product,
  searchOrigin,
  spaceId,
  timestamp,
  tileCardPlaceInProductGrid,
  userId,
  userStyleGenome,
  isMobile
}) => {
  trackEvent({
    event: 'like_dislike_product',
    properties: {
      ...extractStudioDesignProductData({ product }),
      design_id: designId,
      email,
      isFromShop,
      is_showroom: isShowroom,
      like_status: likeStatus, // like or dislike
      primary_referral_source: origin, // studio_hotspot, studio_pdp, shop_pdp, studio_tile_card, shop_tile_card
      product_id_of_pdp_alternative_referral: productIdOfPDPAlternativeReferral,
      product_grid_filter_names: enabledFilters,
      reason_for_dislike: dislikeReason,
      search_origin: searchOrigin,
      studio_id: spaceId,
      studio_genome: userStyleGenome,
      timestamp,
      tile_card_place_in_product_grid: tileCardPlaceInProductGrid,
      user_id: userId,
      is_mobile: isMobile
    }
  });
};

export const trackStudioDisplayShowroom = ({
  studioId
}) => {
  trackEvent({
    event: 'studio_display_showroom',
    properties: {
      studio_id: studioId
    }
  });
};

export const trackTextSearchSubmitted = ({
  designId,
  numberOfResults,
  productId,
  searchValue,
  spaceId,
  userId
}) => {
  trackEvent({
    event: 'studio_text_searched',
    properties: {
      design_id: designId,
      number_of_results: numberOfResults,
      product_id: productId,
      search_term: searchValue,
      studio_id: spaceId,
      user_id: userId
    }
  });
};

export const trackStudioPDPDisplayTooltip = () => {
  trackEvent({
    event: 'studio_pdp_merch_promo_tooltip_clicked',
    properties: {}
  });
};

export const trackStudioPDPDisplayTooltipLearnMore = () => {
  trackEvent({
    event: 'studio_pdp_merch_promo_tootip_expanded',
    properties: {}
  });
};

export const trackStudioBannerClicked = () => {
  trackEvent({
    event: 'studio_banner_clicked',
    properties: {}
  });
};

export const trackStudioBannerCTAClicked = (CTAIndex) => {
  trackEvent({
    event: 'studio_banner_CTA_clicked',
    properties: {'CTA_id': CTAIndex}
  });
};

export const trackStudioRenderFullscreenClick = ({
  spaceId,
  designId,
  designNumber,
  renderNumber
}) => {
  trackEvent({
    event: 'studio_fullscreen_clicked',
    properties: {
      space_id: spaceId,
      design_id: designId,
      design_number: designNumber,
      render_number: renderNumber
    }
  });
};

export const trackStudio360RenderClick = ({
  spaceId,
  designId,
  designNumber,
  renderNumber
}) => {
  trackEvent({
    event: 'studio_360_render_clicked',
    properties: {
      space_id: spaceId,
      design_id: designId,
      design_number: designNumber,
      render_number: renderNumber
    }
  });
};

// View Products
export const trackViewProductsClick = ({
  studio,
  design,
  origin,
  userId
}) => {
  trackEvent({
    event: 'studio_view_products_clicked',
    properties: {
      ...extractStudioDesignProductData({ studio, design }),
      origin, // ('Design Header', 'More Menu')
      user_id: userId
    }
  });
};

export const trackShowFavorites = ({
  design,
  email,
  isMobile,
  studio,
  userId
}) => {
  trackEvent({
    event: 'show_favorites',
    properties: {
      source: 'studio',
      ...extractStudioDesignProductData({ studio, design }),
      email,
      user_id: userId,
      is_mobile: isMobile
    }
  });
};

export const trackTabSelection = ({
  design,
  email,
  studio,
  tabType,
  userId,
  isMobile
}) => {
  trackEvent({
    event: `studio_${tabType}_tab_selected`, // ['designer_note', 'products']
    properties: {
      ...extractStudioDesignProductData({ studio, design }),
      email,
      tab_type: tabType,
      user_id: userId,
      is_mobile: isMobile
    }
  });
};

export const trackShowViewAllDesigns = ({
  design,
  email,
  isMobile,
  studio,
  userId
}) => {
  trackEvent({
    event: `studio_view_all_designs`,
    properties: {
      ...extractStudioDesignProductData({ studio, design }),
      email,
      user_id: userId,
      is_mobile: isMobile
    }
  });
};

// More Menu
export const trackMoreMenuOpen = ({
  studio,
  design,
  userId
}) => {
  trackEvent({
    event: 'studio_more_menu_open',
    properties: {
      ...extractStudioDesignProductData({studio, design}),
      user_id: userId
    }
  });
};

// Share (from More Menu)
export const trackShareFromMoreMenu = ({
  studio,
  design,
  userId
}) => {
  trackEvent({
    event: 'studio_click_share_design',
    properties: {
      ...extractStudioDesignProductData({studio, design}),
      user_id: userId
    }
  });
};

// View Designer Note (from More Menu)
export const trackViewDesignerNoteFromMoreMenu = ({
  studio,
  design,
  userId
}) => {
  trackEvent({
    event: 'studio_view_style_note',
    properties: {
      ...extractStudioDesignProductData({studio, design}),
      user_id: userId
    }
  });
};

// Delete (from More Menu)
export const trackDeleteDesignFromMoreMenu = ({
  studio,
  design,
  userId
}) => {
  trackEvent({
    event: 'studio_delete_user_created_design',
    properties: {
      ...extractStudioDesignProductData({studio, design}),
      user_id: userId
    }
  });
};

// Click Shop from studio
export const trackShopWidgetClick = ({
  studio,
  userId
}) => {
  trackEvent({
    event: 'studio_shop_widget_click',
    properties: {
      ...extractStudioDesignProductData({studio}),
      user_id: userId
    }
  });
};

export const trackDesignSwitch = ({
  designId,
  designNumber,
  isMobile,
  origin,
  spaceId
}) => {
  trackEvent({
    event: 'studio_switch_design',
    properties: {
      design_id: designId,
      design_number: designNumber,
      origin,
      space_id: spaceId,
      is_mobile: isMobile
    }
  });
};

// Space selector switch to different space
export const trackSpaceSelectorNavigate = ({
  studio,
  design,
  userId,
  spaceIdSelected
}) => {
  trackEvent({
    event: 'studio_space_selector_navigate',
    properties: {
      ...extractStudioDesignProductData({studio, design}),
      user_id: userId,
      space_id_selected: spaceIdSelected
    }
  });
};

export const trackMobileHamburgerMenuOptionSelect = ({
  linkName = ''
} = {}) => {
  trackEvent({
    event: 'mobile_hamburger_menu_option_select',
    properties: {
      link_name: linkName
    }
  });
};
